(() => {
  const containerSelector = '[data-stock-out-last="collection"]';
  const renderedKeys = new WeakMap();
  const pendingControllers = new WeakMap();

  const buildPageUrl = (page, sectionId, searchParams = window.location.search) => {
    const url = new URL(window.location.href);
    url.search = searchParams;
    url.searchParams.set('page', page);
    url.searchParams.set('section_id', sectionId);
    return url.toString();
  };

  const getContainerFromHTML = (html) =>
    new DOMParser().parseFromString(html, 'text/html').querySelector(containerSelector);

  const getProductItems = (container) => {
    const grid = container?.querySelector('#product-grid');
    if (!grid) return [];

    return Array.from(grid.children).filter((item) => item.dataset.productId);
  };

  const reorderItems = (items) => {
    const seenProductIds = new Set();
    const availableItems = [];
    const soldOutItems = [];

    items.forEach((item) => {
      const productId = item.dataset.productId;
      if (!productId || seenProductIds.has(productId)) return;

      seenProductIds.add(productId);

      if (item.dataset.productAvailable === 'false') {
        soldOutItems.push(item);
      } else {
        availableItems.push(item);
      }
    });

    return availableItems.concat(soldOutItems);
  };

  const applyAnimationOrder = (items, page, perPage) => {
    const offset = (page - 1) * perPage;

    items.forEach((item, index) => {
      if (item.hasAttribute('style') && item.style.getPropertyValue('--animation-order')) {
        item.style.setProperty('--animation-order', offset + index + 1);
      }
    });
  };

  // Items rebuilt by this script (cloned from a fetched page, or simply
  // moved to a new position) are DOM nodes the theme's scroll-reveal
  // IntersectionObserver never observed. That observer only watches the
  // elements present at page load, so any node not part of that original
  // set stays stuck in the "scroll-trigger" hidden state (opacity: 0)
  // forever, no matter what the user does. Since this script owns the
  // ordering of these items, it takes over responsibility for making them
  // visible instead of leaving that to an observer that will never see them.
  const makeItemVisible = (item) => {
    item.classList.remove('scroll-trigger', 'animate--slide-in', 'animate--fade-in', 'scroll-trigger--offscreen');
    item.removeAttribute('data-cascade');
    return item;
  };

  const renderCollectionPage = async (container, searchParams) => {
    const grid = container.querySelector('#product-grid');
    const sectionId = container.dataset.sectionId || grid?.dataset.id;
    const page = Number(container.dataset.currentPage || 1);
    const totalPages = Number(container.dataset.totalPages || 1);
    const perPage = Number(container.dataset.productsPerPage || 0);

    const renderKey = `${window.location.pathname}?${searchParams ?? window.location.search}|${page}|${totalPages}|${perPage}`;
    if (renderedKeys.get(container) === renderKey) return;

    if (!grid) return;

    const currentItems = reorderItems(getProductItems(container)).map(makeItemVisible);
    if (currentItems.length) {
      applyAnimationOrder(currentItems, page, perPage || currentItems.length);
      grid.replaceChildren(...currentItems);
    }

    if (!sectionId || !perPage || totalPages <= 1) {
      renderedKeys.set(container, renderKey);
      return;
    }

    pendingControllers.get(container)?.abort();
    const controller = new AbortController();
    pendingControllers.set(container, controller);

    const collectionWrapper = container.querySelector('.collection');
    collectionWrapper?.classList.add('loading');

    try {
      const pageContainers = await Promise.all(
        Array.from({ length: totalPages }, async (_, index) => {
          const requestedPage = index + 1;

          if (requestedPage === page) {
            return container;
          }

          const response = await fetch(buildPageUrl(requestedPage, sectionId, searchParams), {
            signal: controller.signal,
          });
          const html = await response.text();
          return getContainerFromHTML(html);
        })
      );

      const allItems = pageContainers.flatMap(getProductItems);
      const orderedItems = reorderItems(allItems);
      const pageItems = orderedItems
        .slice((page - 1) * perPage, page * perPage)
        .map((item) => makeItemVisible(item.cloneNode(true)));

      applyAnimationOrder(pageItems, page, perPage);
      grid.replaceChildren(...pageItems);
      renderedKeys.set(container, renderKey);
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.warn('Could not move sold-out products to the end of the collection pagination.', error);
      }
    } finally {
      if (pendingControllers.get(container) === controller) {
        pendingControllers.delete(container);
      }
      collectionWrapper?.classList.remove('loading');
    }
  };

  const renderAllCollections = () => {
    document.querySelectorAll(containerSelector).forEach(renderCollectionPage);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', renderAllCollections);
  } else {
    renderAllCollections();
  }

  document.addEventListener('product-grid:updated', (event) => {
    const container = event.detail?.container;
    if (container?.matches(containerSelector)) {
      renderCollectionPage(container, event.detail?.searchParams);
    }
  });
})();